import { useEffect, useId, useRef, useState, type ReactNode } from 'react'
import Dialog from '@mui/material/Dialog'
import LinearProgress from '@mui/material/LinearProgress'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Skeleton from '@mui/material/Skeleton'
import Typography from '@mui/material/Typography'
import ArrowForward from '@mui/icons-material/ArrowForward'
import { Theme } from '@mui/material/styles'
import { PaletteInput } from './PaletteInput'

/** What selecting a palette entry does. */
export type PaletteTarget =
    | { kind: 'navigate'; to: string; state?: unknown }
    | { kind: 'href'; href: string; newTab?: boolean }
    | { kind: 'run'; run: () => void }

export interface PaletteItem {
    id: string
    label: string
    /** Second line, such as the owning customer or a serial number */
    secondary?: string
    icon?: ReactNode
    /** Extra terms that match a local item without appearing in its label */
    keywords?: string[]
    target: PaletteTarget
}

export interface PaletteGroup {
    id: string
    label: string
    items: PaletteItem[]
    /** All matches, of which `items` shows the first few */
    total: number
    /** Makes the group header selectable, typically opening the list page filtered by the term */
    viewAll?: PaletteTarget
}

export const PALETTE_MIN_QUERY_LENGTH = 2

const styles = {
    paper: {
        position: 'fixed',
        top: '15%',
        margin: 0,
        // Anchored below the top, so the default max height would let long result lists run off the screen
        maxHeight: '70vh',
    },
    list: {
        overflowY: 'auto',
        padding: 0,
    },
    groupList: {
        padding: 0,
    },
    groupHeader: {
        lineHeight: '32px',
        fontWeight: 600,
        borderRadius: 0,
    },
    groupHeaderText: {
        fontWeight: 600,
        fontSize: '0.8125rem',
    },
    viewAllIcon: {
        fontSize: '0.875rem',
        opacity: 0.5,
    },
    message: {
        padding: (theme: Theme) => theme.spacing(3),
        textAlign: 'center',
        opacity: 0.5,
    },
    hint: {
        padding: (theme: Theme) => theme.spacing(3),
        textAlign: 'center',
        opacity: 0.4,
    },
    skeletonItem: {
        padding: (theme: Theme) => theme.spacing(0.5, 2),
    },
    itemIcon: {
        minWidth: 36,
        color: 'primary.main',
    },
}

export interface SearchPaletteProps {
    open: boolean
    onClose: () => void
    query: string
    onQueryChange: (query: string) => void
    /** Results for the current query; hidden while `loading` so stale matches are never selectable */
    groups: PaletteGroup[]
    loading: boolean
    onSelect: (item: PaletteItem) => void
    /** Entry after all results, such as "Ask Assistant", offered for any non-empty query */
    footerItem?: PaletteItem
    placeholder?: string
    /** Accessible name of the search field */
    label?: string
    /** Shown until the query is long enough to search */
    emptyHint?: string
    minQueryLength?: number
}

/** Identifies the result list a keyboard selection was made in. */
type ListKey = readonly [query: string, groups: PaletteGroup[], loading: boolean]

const isSameList = (a: ListKey, b: ListKey) => a.every((value, index) => Object.is(value, b[index]))

/**
 * The ⌘K search dialog: a search field over grouped results, operated with the arrow keys and Enter. Each group
 * header with a `viewAll` target is selectable as well as its items.
 */
export const SearchPalette = ({
    open,
    onClose,
    query,
    onQueryChange,
    groups,
    loading,
    onSelect,
    footerItem,
    placeholder,
    label = 'Search',
    emptyHint = 'Type to search',
    minQueryLength = PALETTE_MIN_QUERY_LENGTH,
}: SearchPaletteProps) => {
    const listId = useId()
    const selectedRef = useRef<HTMLLIElement>(null)
    const trimmedQuery = query.trim()
    const canSearch = trimmedQuery.length >= minQueryLength
    const visibleGroups = loading || !canSearch ? [] : groups

    const entries: PaletteItem[] = visibleGroups.flatMap((group) => [
        ...(group.viewAll ? [{ id: `${group.id}:all`, label: group.label, target: group.viewAll }] : []),
        ...group.items,
    ])
    if (footerItem) entries.push(footerItem)
    const optionId = (index: number) => `${listId}-option-${index}`
    const hasEntries = entries.length > 0

    // The selection belongs to one result list; a new list starts at the top. Deriving this during render instead of
    // resetting in an effect means a key press that lands right after new results cannot be undone by a late reset.
    const listKey: ListKey = [query, groups, loading]
    const [selection, setSelection] = useState<{ listKey: ListKey; index: number }>({ listKey, index: 0 })
    const selectedIndex = isSameList(selection.listKey, listKey) ? selection.index : 0
    const setSelectedIndex = (next: number | ((index: number) => number)) =>
        setSelection((previous) => {
            const current = isSameList(previous.listKey, listKey) ? previous.index : 0
            return { listKey, index: typeof next === 'function' ? next(current) : next }
        })

    useEffect(() => {
        selectedRef.current?.scrollIntoView?.({ block: 'nearest' })
    }, [selectedIndex])

    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (!hasEntries || event.nativeEvent.isComposing) return
        if (event.key === 'ArrowDown') {
            event.preventDefault()
            setSelectedIndex((index) => (index + 1) % entries.length)
        } else if (event.key === 'ArrowUp') {
            event.preventDefault()
            setSelectedIndex((index) => (index - 1 + entries.length) % entries.length)
        } else if (event.key === 'Enter') {
            event.preventDefault()
            const entry = entries[selectedIndex]
            if (entry) onSelect(entry)
        }
    }

    let nextIndex = 0
    const renderOption = (entry: PaletteItem, content: ReactNode, sx?: object) => {
        const index = nextIndex++
        const isSelected = index === selectedIndex
        return (
            <ListItemButton
                key={entry.id}
                id={optionId(index)}
                component="li"
                role="option"
                aria-selected={isSelected}
                selected={isSelected}
                ref={isSelected ? selectedRef : undefined}
                onClick={() => onSelect(entry)}
                onMouseMove={() => {
                    if (!isSelected) setSelectedIndex(index)
                }}
                dense
                sx={sx}
            >
                {content}
            </ListItemButton>
        )
    }

    const renderItem = (item: PaletteItem, indent: boolean) =>
        renderOption(
            item,
            <>
                {item.icon ? <ListItemIcon sx={styles.itemIcon}>{item.icon}</ListItemIcon> : null}
                <ListItemText
                    primary={item.label}
                    secondary={item.secondary}
                    slotProps={{ secondary: { noWrap: true } }}
                />
            </>,
            indent ? { pl: 4 } : undefined
        )

    const body = (() => {
        if (!trimmedQuery) return <Typography sx={styles.hint}>{emptyHint}</Typography>
        if (!hasEntries) {
            if (!canSearch) return <Typography sx={styles.hint}>{emptyHint}</Typography>
            if (loading) {
                return (
                    <List sx={styles.list}>
                        <ListItem sx={styles.skeletonItem}>
                            <Skeleton variant="text" width={100} />
                        </ListItem>
                        <ListItem sx={styles.skeletonItem}>
                            <Skeleton variant="text" width="60%" height={28} />
                        </ListItem>
                    </List>
                )
            }
            return <Typography sx={styles.message}>No results found</Typography>
        }
        return (
            <List id={listId} role="listbox" aria-label={label} sx={styles.list}>
                {visibleGroups.map((group) => {
                    const headerText = (
                        <ListItemText
                            primary={`${group.label} (${group.total})`}
                            slotProps={{ primary: { sx: styles.groupHeaderText } }}
                        />
                    )
                    return (
                        <li key={group.id} role="presentation">
                            <List role="group" aria-label={group.label} sx={styles.groupList}>
                                {group.viewAll ? (
                                    renderOption(
                                        { id: `${group.id}:all`, label: group.label, target: group.viewAll },
                                        <>
                                            {headerText}
                                            {group.total > group.items.length && (
                                                <ArrowForward sx={styles.viewAllIcon} />
                                            )}
                                        </>,
                                        styles.groupHeader
                                    )
                                ) : (
                                    <ListItem role="presentation" sx={styles.groupHeader} dense>
                                        {headerText}
                                    </ListItem>
                                )}
                                {group.items.map((item) => renderItem(item, true))}
                            </List>
                        </li>
                    )
                })}
                {footerItem ? renderItem(footerItem, false) : null}
            </List>
        )
    })()

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" slotProps={{ paper: { sx: styles.paper } }}>
            <PaletteInput
                value={query}
                onChange={onQueryChange}
                onKeyDown={handleKeyDown}
                placeholder={placeholder}
                label={label}
                inputProps={{
                    role: 'combobox',
                    'aria-autocomplete': 'list',
                    'aria-expanded': hasEntries,
                    'aria-controls': hasEntries ? listId : undefined,
                    'aria-activedescendant': hasEntries ? optionId(selectedIndex) : undefined,
                }}
            />
            {loading && <LinearProgress />}
            {body}
        </Dialog>
    )
}
